// Reads bino-cli's native `metadata.params` ref-parameter declarations
// ($defs.layoutPageParamSpec in bino-cli's document.schema.json) into the
// registry's flat ParamReq display shape. This REPLACES params-from-labels as
// the only source of "declared" params (registry.requires.param.* is retired
// for params; see requires-labels.ts for the still-live dataset half). Pure,
// defensive, never throws — malformed/partial declarations degrade to dropped
// entries or blank fields, never an exception.

import type { ParamReq } from "./derive-requires.js";

/** readParamSpec parses a document's metadata.params (unknown) into ParamReq[]. */
export function readParamSpec(metadataParams: unknown): ParamReq[] {
  if (!Array.isArray(metadataParams)) return [];
  const out: ParamReq[] = [];
  for (const raw of metadataParams) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const p = raw as Record<string, unknown>;
    const name = typeof p.name === "string" ? p.name.trim() : "";
    if (!name) continue; // schema requires name; skip anything without one
    out.push({
      name,
      type: typeof p.type === "string" ? p.type : "",
      default: typeof p.default === "string" ? p.default : "",
      required: p.required === true,
      description: typeof p.description === "string" ? p.description : "",
      ...readOptions(p.options),
    });
  }
  return out;
}

/** readOptions flattens bino-cli's {items,min,max,...} into the CSV/string ParamReq fields. */
function readOptions(v: unknown): { options: string; min: string; max: string } {
  if (!v || typeof v !== "object" || Array.isArray(v)) return { options: "", min: "", max: "" };
  const o = v as Record<string, unknown>;
  const items = Array.isArray(o.items) ? o.items : [];
  const tokens = items
    .filter((it): it is Record<string, unknown> => !!it && typeof it === "object")
    .map((it) => {
      const value = typeof it.value === "string" ? it.value : "";
      const label = typeof it.label === "string" ? it.label : "";
      return label && label !== value ? `${value} (${label})` : value;
    })
    .filter((s) => s !== "");
  const min = typeof o.min === "number" && Number.isFinite(o.min) ? String(o.min) : "";
  const max = typeof o.max === "number" && Number.isFinite(o.max) ? String(o.max) : "";
  return { options: tokens.join(", "), min, max };
}
